import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Typography,
} from '@mui/material';
import moment from 'moment';
import React, { useCallback, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useLoggedInEmployeeId } from '~/components/Auth';
import { CostCenter } from '~/data/costCenters';
import {
  EmploymentDetailsProposalProps,
  EmploymentPositionProposalProps,
  ProposalStatus,
} from '~/data/proposals';
import { EmploymentProposalsDataAndConfig } from '~/features/ScheduledEmploymentChanges/Common/EmploymentProposals';
import { scheduledDateFormats } from '~/features/ScheduledEmploymentChanges/helpers';
import {
  EmploymentUpcomingChanges,
  UpcomingChangesWrapper,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/Common';
import { useUpcomingScheduledEmployment } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/useUpcomingScheduledEmployment';
import {
  AdditionalCostCentersState,
  ChangedStartDateState,
  Form,
  PendingScheduleChanges,
  Restrict,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useScheduleChanges';
import { LegacyDialog } from '~/legacy-components/LegacyDialog';

import {
  CostCenterPercentage,
  FormComponent,
  UnitPositionPercentage,
} from './components';
import { OngoingEmpPositionProposalDialog } from './components/OngoingEmpPositionProposalDialog';
import { useScheduleEmployment } from './useScheduleEmployment';
import { useSxStyles } from './useStyles';

type PropsType = {
  scheduleFormMethods: UseFormReturn<Form>;
  additionalCostCenters: CostCenter[];
  setAdditionalCostCenters: React.Dispatch<
    React.SetStateAction<AdditionalCostCentersState>
  >;
  resetFormAfterProposalDecision: ({
    emplDetPropData,
    empPositionPropData,
  }: {
    emplDetPropData?: EmploymentDetailsProposalProps;
    empPositionPropData?: EmploymentPositionProposalProps;
  }) => void;
  empPositionPending?: PendingScheduleChanges[];
  restrictField: Restrict;
  setChangedStartDate: React.Dispatch<
    React.SetStateAction<ChangedStartDateState>
  >;
  index: number;
  allResponsibleHRIds: number[];
  empId: number;
  proposalsData: EmploymentProposalsDataAndConfig;
};

export const ScheduleEmployment = (props: PropsType) => {
  const {
    scheduleFormMethods,
    additionalCostCenters,
    setAdditionalCostCenters,
    empPositionPending,
    restrictField,
    index,
    setChangedStartDate,
    allResponsibleHRIds,
    empId,
    resetFormAfterProposalDecision,
    proposalsData,
  } = props;

  const { t } = useTranslation();
  const sxStyles = useSxStyles();
  // Fake trigger is used to re-render the component when the unit is changed
  const [fakeTrigger, setFakeTrigger] = useState<number>(0);

  const {
    legalEntities,
    loading,
    allEnums,
    unitSelection,
    onDialogChange,
    activeDialog,
    unitPositions,
    getCostCenterValues,
    onAdditionalCostCenterAdd,
    deleteSelectedCC,
    onUnitChange,
    empData,
    managers,
    allEmployees,
    onLegalEntityChange,
    warningOpen,
    setWarningOpen,
    orgTree,
    defaultData,
    upcomingChangesOpen,
    setUpComingChangesOpen,
    proposals,
    newEngine,
    onProposalDecision,
    isHRUser,
    customFields,
  } = useScheduleEmployment({
    scheduleFormMethods,
    index,
    setAdditionalCostCenters,
    additionalCostCenters,
    empId,
    resetFormAfterProposalDecision,
    empPositionPending: empPositionPending?.[index]?.empPosition,
    proposalsData,
  });

  const currentEmplWF = newEngine && index === 0;
  const loggedInUserId = useLoggedInEmployeeId();
  const hrSelectorDisable =
    isHRUser && allResponsibleHRIds.includes(loggedInUserId);
  const [showProposal, setShowProposal] =
    useState<EmploymentPositionProposalProps | null>(null);

  const {
    proposalChanges,
    upcomingChanges,
    isLoading: isLoadingUpcomingChanges,
  } = useUpcomingScheduledEmployment({
    proposals,
    proposalWorkflowsEnabled: currentEmplWF,
    defaultData,
    pendingScheduleChanges: {
      index,
      pendingData: empPositionPending,
    },
    fieldValueFormatProps: {
      legalEntities,
      unitSelection,
      allEnums,
      allEmployees,
      managers,
    },
  });

  const handleOnUnitChange = (newValue: number) => {
    onUnitChange(newValue);
    // Fake trigger is used to re-render the component when the unit is changed
    setFakeTrigger(fakeTrigger + 1);
  };

  const proposalUpcomingChanges = proposalChanges.upcoming.filter(
    (proposalUpcomingChange) => proposalUpcomingChange.valueChanges.length,
  );

  const UpcomingChangesComponent = useCallback(() => {
    if (
      !currentEmplWF &&
      !!empPositionPending?.[index]?.empPosition &&
      defaultData &&
      upcomingChanges.length
    ) {
      return (
        <UpcomingChangesWrapper
          upcomingChangesOpen={upcomingChangesOpen}
          setUpComingChangesOpen={setUpComingChangesOpen}
        >
          <EmploymentUpcomingChanges
            label={moment(
              empPositionPending?.[index]?.empPosition
                .fInternalPositionStartDate,
              scheduledDateFormats.dashFormat,
            ).format(scheduledDateFormats.dotFormat)}
            valueChanges={upcomingChanges}
          />
        </UpcomingChangesWrapper>
      );
    }
    if (currentEmplWF && proposalUpcomingChanges.length > 0) {
      return (
        <UpcomingChangesWrapper
          upcomingChangesOpen={upcomingChangesOpen}
          setUpComingChangesOpen={setUpComingChangesOpen}
        >
          {proposalUpcomingChanges.map((upcomingChanges, i) => {
            return (
              <EmploymentUpcomingChanges
                key={`upcoming-${i}`}
                {...upcomingChanges}
              />
            );
          })}
        </UpcomingChangesWrapper>
      );
    }
    return null;
  }, [
    currentEmplWF,
    defaultData,
    empPositionPending,
    index,
    setUpComingChangesOpen,
    upcomingChangesOpen,
    upcomingChanges,
    proposalUpcomingChanges,
  ]);
  const OngoingProposals = () => {
    if (!!proposalChanges.onGoing) {
      const { proposalId, label, valueChanges } = proposalChanges.onGoing;
      const onGoingProposal = proposals.find(
        (p) => p.proposalId === proposalId,
      );

      return (
        <>
          <EmploymentUpcomingChanges label={label} valueChanges={valueChanges}>
            {!!onGoingProposal && (
              <Button
                size="small"
                variant="contained"
                onClick={() => setShowProposal(onGoingProposal)}
              >
                {t('seeWorkflowStatus')}
              </Button>
            )}
          </EmploymentUpcomingChanges>
          {!!showProposal && (
            <OngoingEmpPositionProposalDialog
              open
              proposal={showProposal}
              onClose={() => setShowProposal(null)}
              onSubmit={onProposalDecision}
            />
          )}
        </>
      );
    }

    return null;
  };

  return (
    <Box>
      <Typography sx={{ fontWeight: 'bold', fontSize: '16px' }}>
        {`${t('laSchedule')} - ${t('laEmployment')}`}
      </Typography>
      {loading || !empData ? (
        <Box sx={sxStyles.loadingMiddle}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={sxStyles.internalPositionLayout}>
          <FormComponent
            index={index}
            deleteSelectedCC={deleteSelectedCC}
            restrictField={restrictField}
            scheduleFormMethods={scheduleFormMethods}
            getCostCenterValues={getCostCenterValues}
            empData={empData}
            legalEntities={legalEntities}
            allEnums={allEnums}
            onUnitChange={handleOnUnitChange}
            unitPositions={unitPositions}
            unitSelection={unitSelection}
            onDialogChange={onDialogChange}
            managers={managers!}
            allEmployees={allEmployees}
            onLegalEntityChange={onLegalEntityChange}
            setWarningOpen={setWarningOpen}
            orgTree={orgTree}
            newEmpl={!empPositionPending?.[index] && index !== 0}
            setChangedStartDate={setChangedStartDate}
            allResponsibleHRIds={allResponsibleHRIds}
            hrSelectorDisable={hrSelectorDisable}
            pendingProposal={
              currentEmplWF &&
              proposals.length > 0 &&
              proposals[0].proposalStatus === ProposalStatus.IN_PROGRESS
            }
            inProgressPropStartDate={
              proposals.length > 0 && currentEmplWF
                ? proposals[0].proposalData.ip.startDate
                : undefined
            }
            newEngine={newEngine}
          />
          <Collapse
            in={activeDialog.includes('costCenter')}
            unmountOnExit
            easing={'easing'}
            timeout={500}
          >
            <CostCenterPercentage
              customFields={customFields}
              getCostCenterValues={getCostCenterValues}
              // @ts-ignore
              selectedCostCenterId={scheduleFormMethods.getValues(
                `schedule.${index}.empPosition.fEmpCostCenter`,
              )}
              scheduleFormMethods={scheduleFormMethods}
              onAdditionalCostCenterAdd={onAdditionalCostCenterAdd}
              deleteSelectedCC={deleteSelectedCC}
              additionalCostCenters={additionalCostCenters}
              index={index}
            />
          </Collapse>
          <Collapse
            in={activeDialog.includes('unitPosition')}
            unmountOnExit
            easing={'easing'}
            timeout={500}
          >
            <UnitPositionPercentage
              unitPositions={unitPositions}
              selectedUnitPositionIds={scheduleFormMethods.getValues(
                `schedule.${index}.empPosition.fEmpUnitPositions`,
              )}
              scheduleFormMethods={scheduleFormMethods}
              allEnums={allEnums}
              index={index}
            />
          </Collapse>
          {isLoadingUpcomingChanges ? (
            <Box sx={sxStyles.loadingMiddle}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <OngoingProposals />
              <UpcomingChangesComponent />
            </>
          )}
        </Box>
      )}
      <LegacyDialog
        open={warningOpen}
        onClose={() => setWarningOpen(false)}
        variant="warning"
        title={t('laNotice')}
        showSave={false}
        showCancel
        customCancelText={t('laClose')}
      >
        {t('laBackdatingWarningMsgIntro')}
        <ul>
          <li>{t('laBackdatingWarningMsg1')}</li>
          <li>{t('laBackdatingWarningMsg3')}</li>
        </ul>
      </LegacyDialog>
    </Box>
  );
};
